/**
 * Copyright 2019, SumUp Ltd.
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import React from 'react';
import { css } from '@emotion/core';
import styled from '../../styles/styled';
import { uniqueId } from '../../util/id';
import { Text } from '../Text/Text';
import { Switch } from './components/Switch/Switch';
const textWrapperStyles = ({ theme }) => css `
  label: toggle__text-wrapper;
  display: block;
  margin-left: ${theme.spacings.kilo};
  cursor: pointer;

  ${theme.mq.untilKilo} {
    margin-left: 0;
    margin-right: ${theme.spacings.kilo};
  }
`;
const ToggleTextWrapper = styled('label')(textWrapperStyles);
const labelStyles = css `
  label: toggle__label;
  padding-top: 2px;
`;
const ToggleLabel = styled(Text)(labelStyles);
const explanationStyles = ({ theme }) => css `
  label: toggle__explanation;
  color: ${theme.colors.n700};
`;
const ToggleExplanation = styled(Text)(explanationStyles);
const toggleWrapperStyles = ({ theme }) => css `
  label: toggle;
  display: flex;
  align-items: flex-start;
  margin-bottom: ${theme.spacings.mega};

  ${theme.mq.untilKilo} {
    flex-direction: row-reverse;
    justify-content: space-between;
  }
`;
const toggleWrapperNoMarginStyles = ({ noMargin }) => noMargin &&
    css `
    label: toggle--no-margin;
    margin-bottom: 0;
  `;
const ToggleWrapper = styled('div')(toggleWrapperStyles, toggleWrapperNoMarginStyles);
/**
 * A toggle component with support for labels and additional explanations.
 */
export const Toggle = React.forwardRef(({ label, explanation, noMargin, ...props }, ref) => {
    const switchId = uniqueId('toggle-switch_');
    const labelId = uniqueId('toggle-label_');
    return (React.createElement(ToggleWrapper, Object.assign({}, { noMargin }),
        React.createElement(Switch, Object.assign({}, props, { "aria-labelledby": labelId, id: switchId, ref: ref })),
        (label || explanation) && (React.createElement(ToggleTextWrapper, { id: labelId, htmlFor: switchId },
            label && (React.createElement(ToggleLabel, { size: "kilo", noMargin: true }, label)),
            explanation && (React.createElement(ToggleExplanation, { size: "kilo", noMargin: true }, explanation))))));
});
Toggle.displayName = 'Toggle';
